import {
    createAsyncThunk,
    createSlice,
} from "@reduxjs/toolkit";

import API
    from "../../utils/apiCalling";

import {
    config,
} from "../../utils/apiUrl";


const api =
    new API({
        tokenKey:
            "platformAccessToken",
    });


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {

    /*
    |--------------------------------------------------------------------------
    | LIST
    |--------------------------------------------------------------------------
    */

    activities: [],

    pagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 1,
    },

    filters: {
        page: 1,
        limit: 20,
        search: "",
        action: "",
        entityType: "",
        status: "",
        tenantId: "",
        platformAdminId: "",
        fromDate: "",
        toDate: "",
    },

    listLoading: false,
    listError: null,


    /*
    |--------------------------------------------------------------------------
    | FILTER OPTIONS
    |--------------------------------------------------------------------------
    */

    filterOptions: {
        actions: [],
        entityTypes: [],
    },

    filtersLoading: false,
    filtersError: null,


    /*
    |--------------------------------------------------------------------------
    | DETAILS
    |--------------------------------------------------------------------------
    */

    selectedActivity: null,

    detailsLoading: false,
    detailsError: null,
};


/*
|--------------------------------------------------------------------------
| BUILD QUERY STRING
|--------------------------------------------------------------------------
*/

const buildQueryString = (
    params = {}
) => {

    const searchParams =
        new URLSearchParams();


    Object.entries(
        params
    ).forEach(
        ([
            key,
            value,
        ]) => {

            if (
                value === undefined ||
                value === null ||
                value === ""
            ) {

                return;

            }


            searchParams.append(
                key,
                value
            );

        }
    );


    return searchParams
        .toString();

};


/*
|--------------------------------------------------------------------------
| NORMALIZE ERROR
|--------------------------------------------------------------------------
*/

const getErrorPayload = (
    error
) => {

    return {
        message:
            error?.response?.data?.message ||
            error?.response?.message ||
            error?.message ||
            "Something went wrong.",
    };

};


/*
|--------------------------------------------------------------------------
| FETCH PLATFORM ACTIVITY
|--------------------------------------------------------------------------
*/

export const fetchPlatformActivity =
    createAsyncThunk(
        "platformActivity/fetchPlatformActivity",

        async (
            params = {},
            {
                rejectWithValue,
            }
        ) => {

            try {

                const query =
                    buildQueryString(
                        params
                    );


                const url =
                    query
                        ? `${config.platformActivity}?${query}`
                        : config.platformActivity;


                const response =
                    await api.get(
                        url,
                        true,
                        "platform"
                    );


                return response;

            } catch (
                error
            ) {

                return rejectWithValue(
                    getErrorPayload(
                        error
                    )
                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| FETCH ACTIVITY FILTERS
|--------------------------------------------------------------------------
*/

export const fetchPlatformActivityFilters =
    createAsyncThunk(
        "platformActivity/fetchPlatformActivityFilters",

        async (
            _,
            {
                rejectWithValue,
            }
        ) => {

            try {

                const response =
                    await api.get(
                        config
                            .platformActivityFilters,
                        true
                    );


                return response;

            } catch (
                error
            ) {

                return rejectWithValue(
                    getErrorPayload(
                        error
                    )
                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| FETCH ACTIVITY DETAILS
|--------------------------------------------------------------------------
*/

export const fetchPlatformActivityById =
    createAsyncThunk(
        "platformActivity/fetchPlatformActivityById",

        async (
            id,
            {
                rejectWithValue,
            }
        ) => {

            try {

                const response =
                    await api.get(
                        config
                            .platformActivityById(
                                id
                            ),
                        true
                    );


                return response;

            } catch (
                error
            ) {

                return rejectWithValue(
                    getErrorPayload(
                        error
                    )
                );

            }

        }
    );


/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const platformActivitySlice =
    createSlice({

        name:
            "platformActivity",

        initialState,

        reducers: {

            /*
            |--------------------------------------------------------------------------
            | SET FILTER
            |--------------------------------------------------------------------------
            */

            setPlatformActivityFilter:
                (
                    state,
                    action
                ) => {

                    const {
                        key,
                        value,
                    } =
                        action.payload ||
                        {};


                    if (
                        !key
                    ) {

                        return;

                    }


                    state.filters[
                        key
                    ] =
                        value;

                },


            /*
            |--------------------------------------------------------------------------
            | SET FILTERS
            |--------------------------------------------------------------------------
            */

            setPlatformActivityFilters:
                (
                    state,
                    action
                ) => {

                    state.filters = {
                        ...state.filters,
                        ...(action.payload || {}),
                    };

                },


            /*
            |--------------------------------------------------------------------------
            | RESET FILTERS
            |--------------------------------------------------------------------------
            */

            resetPlatformActivityFilters:
                (
                    state
                ) => {

                    state.filters = {
                        page: 1,
                        limit: 20,
                        search: "",
                        action: "",
                        entityType: "",
                        status: "",
                        tenantId: "",
                        platformAdminId: "",
                        fromDate: "",
                        toDate: "",
                    };

                },


            /*
            |--------------------------------------------------------------------------
            | CLEAR SELECTED
            |--------------------------------------------------------------------------
            */

            clearSelectedPlatformActivity:
                (
                    state
                ) => {

                    state.selectedActivity =
                        null;

                    state.detailsError =
                        null;

                },


            /*
            |--------------------------------------------------------------------------
            | CLEAR ERRORS
            |--------------------------------------------------------------------------
            */

            clearPlatformActivityErrors:
                (
                    state
                ) => {

                    state.listError =
                        null;

                    state.filtersError =
                        null;

                    state.detailsError =
                        null;

                },

        },


        /*
        |--------------------------------------------------------------------------
        | EXTRA REDUCERS
        |--------------------------------------------------------------------------
        */

        extraReducers:
            (
                builder
            ) => {

                /*
                |--------------------------------------------------------------------------
                | LIST
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        fetchPlatformActivity
                            .pending,

                        (
                            state
                        ) => {

                            state.listLoading =
                                true;

                            state.listError =
                                null;

                        }
                    )
                    .addCase(
                        fetchPlatformActivity
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.listLoading =
                                false;


                            const payload =
                                action.payload ||
                                {};


                            state.activities =
                                Array.isArray(
                                    payload?.data
                                )
                                    ? payload.data
                                    : [];


                            state.pagination = {
                                page:
                                    payload
                                        ?.pagination
                                        ?.page ||
                                    state.filters
                                        .page ||
                                    1,

                                limit:
                                    payload
                                        ?.pagination
                                        ?.limit ||
                                    state.filters
                                        .limit ||
                                    20,

                                total:
                                    payload
                                        ?.pagination
                                        ?.total ||
                                    0,

                                totalPages:
                                    payload
                                        ?.pagination
                                        ?.totalPages ||
                                    1,
                            };

                        }
                    )
                    .addCase(
                        fetchPlatformActivity
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state.listLoading =
                                false;

                            state.listError =
                                action.payload ||
                                {
                                    message:
                                        "Unable to load platform activity.",
                                };

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | FILTER OPTIONS
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        fetchPlatformActivityFilters
                            .pending,

                        (
                            state
                        ) => {

                            state.filtersLoading =
                                true;

                            state.filtersError =
                                null;

                        }
                    )
                    .addCase(
                        fetchPlatformActivityFilters
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.filtersLoading =
                                false;


                            const payload =
                                action.payload ||
                                {};


                            state.filterOptions = {
                                actions:
                                    Array.isArray(
                                        payload
                                            ?.data
                                            ?.actions
                                    )
                                        ? payload
                                            .data
                                            .actions
                                        : [],

                                entityTypes:
                                    Array.isArray(
                                        payload
                                            ?.data
                                            ?.entityTypes
                                    )
                                        ? payload
                                            .data
                                            .entityTypes
                                        : [],
                            };

                        }
                    )
                    .addCase(
                        fetchPlatformActivityFilters
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state.filtersLoading =
                                false;

                            state.filtersError =
                                action.payload ||
                                {
                                    message:
                                        "Unable to load activity filters.",
                                };

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | DETAILS
                |--------------------------------------------------------------------------
                */

                builder
                    .addCase(
                        fetchPlatformActivityById
                            .pending,

                        (
                            state
                        ) => {

                            state.detailsLoading =
                                true;

                            state.detailsError =
                                null;

                        }
                    )
                    .addCase(
                        fetchPlatformActivityById
                            .fulfilled,

                        (
                            state,
                            action
                        ) => {

                            state.detailsLoading =
                                false;

                            state.selectedActivity =
                                action
                                    .payload
                                    ?.data ||
                                null;

                        }
                    )
                    .addCase(
                        fetchPlatformActivityById
                            .rejected,

                        (
                            state,
                            action
                        ) => {

                            state.detailsLoading =
                                false;

                            state.detailsError =
                                action.payload ||
                                {
                                    message:
                                        "Unable to load activity details.",
                                };

                        }
                    );

            },

    });


/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
    setPlatformActivityFilter,
    setPlatformActivityFilters,
    resetPlatformActivityFilters,
    clearSelectedPlatformActivity,
    clearPlatformActivityErrors,
} =
    platformActivitySlice.actions;


/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const selectPlatformActivities =
    (
        state
    ) =>
        state
            .platformActivity
            ?.activities ||
        [];


export const selectPlatformActivityPagination =
    (
        state
    ) =>
        state
            .platformActivity
            ?.pagination ||
        initialState.pagination;


export const selectPlatformActivityFilters =
    (
        state
    ) =>
        state
            .platformActivity
            ?.filters ||
        initialState.filters;


export const selectPlatformActivityFilterOptions =
    (
        state
    ) =>
        state
            .platformActivity
            ?.filterOptions ||
        initialState.filterOptions;


export const selectPlatformActivityListLoading =
    (
        state
    ) =>
        Boolean(
            state
                .platformActivity
                ?.listLoading
        );


export const selectPlatformActivityFiltersLoading =
    (
        state
    ) =>
        Boolean(
            state
                .platformActivity
                ?.filtersLoading
        );


export const selectSelectedPlatformActivity =
    (
        state
    ) =>
        state
            .platformActivity
            ?.selectedActivity ||
        null;


export const selectPlatformActivityDetailsLoading =
    (
        state
    ) =>
        Boolean(
            state
                .platformActivity
                ?.detailsLoading
        );


export const selectPlatformActivityListError =
    (
        state
    ) =>
        state
            .platformActivity
            ?.listError ||
        null;


export const selectPlatformActivityDetailsError =
    (
        state
    ) =>
        state
            .platformActivity
            ?.detailsError ||
        null;


/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default platformActivitySlice.reducer;